import { useState, useEffect } from "react";
import { Plus, RefreshCw, AlertCircle } from "lucide-react";
import { toast } from "react-toastify";
import SupportConfirmationModal from "./SupportConfirmationModal";
import {
  getWorkflowTags,
  createWorkflowTag,
  updateWorkflowTag,
  deleteWorkflowTag,
  TAXONOMY_SLUG_PATTERN,
  TAXONOMY_SLUG_MAX,
  WORKFLOW_TAG_NAME_MAX,
  TAG_COLOR_PATTERN,
  readApiError,
} from "./supportApi";

const DEFAULT_COLOR = "#3366ff";

/** "Needs Refund" -> "needs-refund". A starting point, still editable. */
const slugify = (name) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, TAXONOMY_SLUG_MAX);

export default function WorkflowTags() {
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  // The tag being edited, held by the slug it had when the form opened — that is what the
  // PATCH path uses, even when the form changes the slug.
  const [editingSlug, setEditingSlug] = useState(null);
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  // Colour is optional on the server, so the form tracks "has one" separately from
  // the swatch value — an <input type="color"> can never be empty.
  const [hasColor, setHasColor] = useState(true);
  const [color, setColor] = useState(DEFAULT_COLOR);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [tagToDelete, setTagToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const fetchTags = async () => {
    setIsLoading(true);
    try {
      const data = await getWorkflowTags();
      setTags(data.tags || []);
    } catch (error) {
      console.error("Error fetching workflow tags:", error);
      toast.error("Failed to load tags");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const resetForm = () => {
    setEditingSlug(null);
    setName("");
    setSlug("");
    setSlugTouched(false);
    setHasColor(true);
    setColor(DEFAULT_COLOR);
    setShowForm(false);
  };

  const startEditing = (tag) => {
    setEditingSlug(tag.slug);
    setName(tag.name);
    setSlug(tag.slug);
    // Already named, so typing in it must not rewrite the slug underneath.
    setSlugTouched(true);
    setHasColor(!!tag.color);
    setColor(tag.color || DEFAULT_COLOR);
    setShowForm(true);
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const response = await deleteWorkflowTag(tagToDelete.slug);
      if (response.ok || response.status === 204) {
        toast.success("Tag deleted");
        setTagToDelete(null);
        await fetchTags();
        return;
      }
      toast.error(await readApiError(response, "Could not delete the tag"));
    } catch (error) {
      console.error("Error deleting workflow tag:", error);
      toast.error("Could not delete the tag. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleNameChange = (value) => {
    setName(value);
    if (!slugTouched) setSlug(slugify(value));
  };

  const slugIsValid = TAXONOMY_SLUG_PATTERN.test(slug);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    if (!slugIsValid) {
      toast.error(
        "Slug may contain lowercase letters, numbers and single hyphens only",
      );
      return;
    }
    if (hasColor && !TAG_COLOR_PATTERN.test(color)) {
      toast.error("Colour must be a hex value like #3366ff");
      return;
    }

    setIsSubmitting(true);
    try {
      // On an edit, color must go every time: leaving the key out keeps the old colour, so
      // "no colour" has to be sent as an explicit null.
      const payload = { name: name.trim(), slug, color: hasColor ? color : null };
      if (!editingSlug && !hasColor) delete payload.color;

      const response = editingSlug
        ? await updateWorkflowTag(editingSlug, payload)
        : await createWorkflowTag(payload);
      if (response.ok) {
        toast.success(editingSlug ? "Tag updated" : "Tag created");
        resetForm();
        await fetchTags();
        return;
      }
      toast.error(await readApiError(response, "Could not save the tag"));
    } catch (error) {
      console.error("Error saving workflow tag:", error);
      toast.error("Could not save the tag. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass =
    "rounded-md border border-border/70 bg-white/[0.03] px-2.5 py-1.5 text-xs text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30";

  return (
    <div className="">
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
          <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
          Support
        </div>
        <h1 className="font-serif italic text-[36px] leading-[1.05] text-foreground mb-1">
          Workflow <span className="text-gradient-ginox">Tags</span>
        </h1>
        <p className="text-sm text-muted-foreground mb-4">
          Labels for tickets and chats — how work is sorted internally. Separate from
          article tags, which describe content.
        </p>
      </div>

      <div className="max-w-4xl space-y-5">
        {!showForm && (
          <button
            type="button"
            onClick={() => setShowForm(true)}
            className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors flex items-center gap-1.5"
          >
            <Plus className="h-3.5 w-3.5" />
            Add tag
          </button>
        )}

        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="rounded-lg border border-border/70 bg-card p-5 space-y-4"
          >
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-muted-foreground">
              {editingSlug ? "Edit tag" : "New tag"}
            </p>

            <div className="md:flex md:gap-4 flex flex-col gap-4">
              <div className="flex-1 flex flex-col">
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  maxLength={WORKFLOW_TAG_NAME_MAX}
                  placeholder="Needs refund"
                  className={inputClass}
                />
              </div>
              <div className="flex-1 flex flex-col">
                <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                  Slug
                </label>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => {
                    setSlugTouched(true);
                    setSlug(e.target.value);
                  }}
                  maxLength={TAXONOMY_SLUG_MAX}
                  placeholder="needs-refund"
                  className={`${inputClass} font-mono ${
                    slug && !slugIsValid ? "border-red-500/50" : ""
                  }`}
                />
                {slug && !slugIsValid && (
                  <span className="mt-1 text-[10px] text-red-400">
                    Lowercase letters, numbers and single hyphens only.
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-col">
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                Colour
              </label>
              <div className="flex items-center gap-3">
                <input
                  type="color"
                  value={color}
                  disabled={!hasColor}
                  onChange={(e) => setColor(e.target.value)}
                  className="h-8 w-14 cursor-pointer rounded-md border border-border/70 bg-white/[0.03] disabled:opacity-40 disabled:cursor-not-allowed"
                />
                <span className="font-mono text-xs text-muted-foreground">
                  {hasColor ? color : "none"}
                </span>
                <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                  <input
                    type="checkbox"
                    checked={!hasColor}
                    onChange={(e) => setHasColor(!e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-border/70 bg-white/[0.03] text-ginox-green focus:ring-ginox-green/30"
                  />
                  No colour
                </label>
              </div>
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                disabled={isSubmitting}
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-3 py-1.5 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {isSubmitting && <RefreshCw className="h-3.5 w-3.5 animate-spin" />}
                {isSubmitting ? "Saving..." : editingSlug ? "Save" : "Create"}
              </button>
              <button
                type="button"
                onClick={resetForm}
                disabled={isSubmitting}
                className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors disabled:opacity-40"
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {isLoading ? (
          <div className="py-10 flex justify-center">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-ginox-green" />
          </div>
        ) : tags.length === 0 ? (
          <div className="flex items-start gap-2 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-lg">
            <AlertCircle
              className="h-4 w-4 text-yellow-400 mt-0.5 shrink-0"
              aria-hidden="true"
            />
            <p className="text-yellow-400 text-sm">
              No tags yet. Add one above, then apply it from a ticket.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-separate border-spacing-0 rounded-lg border border-border/70 bg-card overflow-hidden">
              <thead>
                <tr>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Name
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Slug
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Colour
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-left text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Used on
                  </th>
                  <th className="border-b border-border/70 px-4 py-3 text-center text-[10px] font-medium uppercase tracking-[1.5px] text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {tags.map((tag) => (
                  <tr key={tag.slug} className="hover:bg-white/[0.04] transition">
                    <td className="border-b border-border/40 px-4 py-3 text-sm text-foreground">
                      {tag.name}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3 text-sm font-mono text-muted-foreground">
                      {tag.slug}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3">
                      {tag.color ? (
                        <span className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                          <span
                            className="inline-block h-3.5 w-3.5 rounded border border-border/70"
                            style={{ backgroundColor: tag.color }}
                          />
                          {tag.color}
                        </span>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">—</span>
                      )}
                    </td>
                    <td className="border-b border-border/40 px-4 py-3 text-sm tabular-nums text-muted-foreground">
                      {tag.ticketCount ?? 0} tickets · {tag.chatCount ?? 0} chats
                    </td>
                    <td className="border-b border-border/40 px-4 py-3">
                      <div className="flex justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => startEditing(tag)}
                          className="rounded-md border border-border/70 bg-white/[0.03] px-3 py-1.5 text-xs text-muted-foreground hover:bg-white/[0.05] hover:text-foreground transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => setTagToDelete(tag)}
                          className="rounded-md border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-xs text-red-400 hover:bg-red-500/20 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Deleting a tag takes it off everything carrying it, so the counts are the warning. */}
      <SupportConfirmationModal
        isOpen={!!tagToDelete}
        onClose={() => setTagToDelete(null)}
        onConfirm={handleDelete}
        title="Delete Tag"
        message={
          tagToDelete
            ? `Delete "${tagToDelete.name}"? It will be removed from ${
                tagToDelete.ticketCount ?? 0
              } ticket(s) and ${tagToDelete.chatCount ?? 0} chat(s). This cannot be undone.`
            : ""
        }
        confirmText="Yes, Delete"
        cancelText="No, Cancel"
        loading={isDeleting}
        loadingText="Deleting..."
      />
    </div>
  );
}
